<template>
  <div class="flex items-center" :class="clsx(showBtnText ? 'gap-4 px-1 py-0.5' : 'gap-2')">
    <template v-for="item in topicInsertItems" :key="item.key">
      <shortcut-tooltip :title="item.name" :description="item.tooltip" :shortcut="item.shortcut" align="bottom">
        <tool-bar-button
          data-focus="not-allow"
          :icon-name="item.icon"
          :disabled="!item.enable"
          :label="item.name"
          :size="buttonSize"
          :debounce-disable-status="true"
          @click.stop.prevent="item.action"
        />
      </shortcut-tooltip>
    </template>

    <div v-if="topicInsertItems.length && relationshipItems.length" :class="clsx('h-4', showBtnText ? '' : 'mx-1')">
      <fw-divider class="border-divider-onContainer-light dark:border-divider-onContainer-dark h-full" orientation="vertical" />
    </div>

    <template v-for="item in relationshipItems" :key="item.key">
      <shortcut-tooltip :title="item.name" :description="item.tooltip" :shortcut="item.shortcut" align="bottom">
        <tool-bar-button
          data-focus="not-allow"
          :icon-name="item.icon"
          :disabled="!item.enable"
          :label="item.name"
          :size="buttonSize"
          :debounce-disable-status="true"
          @click.stop.prevent="item.action"
        />
      </shortcut-tooltip>
    </template>

    <div v-if="relationshipItems.length && insertItemExtras.length" :class="clsx('h-4', showBtnText ? '' : 'mx-1')">
      <fw-divider class="border-divider-onContainer-light dark:border-divider-onContainer-dark h-full" orientation="vertical" />
    </div>

    <template v-for="item in insertItemExtras" :key="item.key">
      <shortcut-tooltip :title="item.name" :description="item.tooltip" :shortcut="item.shortcut" align="bottom">
        <tool-bar-button
          data-focus="not-allow"
          :icon-name="item.icon"
          :disabled="!item.enable"
          :label="item.name"
          :size="buttonSize"
          :debounce-disable-status="true"
          @click.stop.prevent="item.action"
        />
      </shortcut-tooltip>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, watchEffect, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { clsx } from 'clsx'
import ShortcutTooltip from 'client/components/shortcut-tooltip.vue'
import ToolBarButton from 'app/fragments/shared/editor-top-bar/tool-bar-button.vue'
import { useFrameStore } from 'app/services/pinia/frame-store'
import { useWindowResize } from 'client/composables/window-resize'
import { useEmbedToolBarItems } from '../../composables/use-embed-toolbar-items'
import { useGlobalConfig } from '../../../composables/use-global-config'

const { topicInsertToolbarItems, topicModifyToolbarItems, topicInsertItemToolbarItems } = useEmbedToolBarItems()
const frameStore = useFrameStore()
const { isToolbarTextVisible: showBtnText } = storeToRefs(frameStore)
const { embedEditorPreset, embedEditorPresetType } = storeToRefs(useGlobalConfig())

const { windowWidth } = useWindowResize()
const isNarrowScreen = computed(() => windowWidth.value < 600)
const originalShowBtnText = showBtnText.value

const isMakerPreset = computed(() => embedEditorPresetType.value === 'maker')

watchEffect(() => {
  if (isMakerPreset.value && isNarrowScreen.value) {
    frameStore.setToolbarTextVisible(false)
  } else if (isMakerPreset.value) {
    frameStore.setToolbarTextVisible(originalShowBtnText)
  }
})

onUnmounted(() => {
  if (isMakerPreset.value) {
    frameStore.setToolbarTextVisible(originalShowBtnText)
  }
})

const buttonSize = computed(() => {
  return isNarrowScreen.value && isMakerPreset.value ? 'large' : 'small'
})

const topicInsertItems = computed(() => topicInsertToolbarItems.value)
const relationshipItems = computed(() => topicModifyToolbarItems.value)

const allowedInsertItemKeys = computed(() => (embedEditorPreset.value.enableNoteLabel ? new Set(['NOTE', 'LABEL']) : new Set([])))
const insertItemExtras = computed(() => topicInsertItemToolbarItems.value.filter(item => allowedInsertItemKeys.value.has(item.key)))
</script>
